import { TopconRoute, fakeT as t } from '@topcon/topcon-utils';
import { ReactNode, useEffect, useMemo } from 'react';
import { IconName } from '@topcon/tuicl';
import { useTranslation } from 'react-i18next';

import NotFound from '../../pages/NotFound/NotFound';
import Settings from '../../pages/Settings/Settings';
import { AppRoutes, useHideUnclaimedOrganizations, useHideCustomerSuggestions } from './utils';
import { hideUnlessAccessible } from './access';
import Divisions from '../../pages/Divisions/Divisions';
import Overview from '../../pages/Overview/Overview';
import Users from '../../pages/UserManagement/Users/Users';
import Requests from '../../pages/UserManagement/Requests/Requests';
import Playground from '../../pages/Playground/Playground';
import GeneralInformation from '../../pages/Settings/GeneralInformation';
import Access from '../../pages/Settings/Accessibility';
import DeactivateAccount from '../../pages/Settings/DeactivateAccount';
import DomainRegistration from '../../pages/Settings/DomainRegistration/DomainRegistration';
import IdentityProviders from '../../pages/Settings/IdentityProviders/IdentityProviders';
import DevSettings from '../../pages/Settings/DevSettings';
import Companies from '../../pages/Companies/Companies';
import CompaniesList from '../../pages/Companies/CompaniesList';
import JoinRequests from '../../pages/Companies/JoinRequests';
import Invites from '../../pages/Companies/Invites';
import AboutMe from '../../pages/AboutMe';
import Dealers from '../../pages/Settings/Dealers/Dealers';
import ServiceAgreement from '../../pages/ServiceAgreement/ServiceAgreement';
import Customers from '../../pages/Customers/Customers';
import CustomerSuggestions from '../../pages/Customers/CustomerSuggestions';
import CustomerInvites from '../../pages/Customers/Invites/CustomerInvites';
import CustomerRequests from '../../pages/Customers/Requests/CustomerRequests';
import CustomersHub from '../../pages/Customers/CustomersHub';
import Organizations from '../../pages/Organizations/Organizations';
import TopconId from '../../pages/TopconId/TopconId';
import Support from '../../pages/Support/Support';
import Notifications from '../../pages/Notifications/Notifications';
import Authorize from '../../components/Authorize';
import NotificationSettings from '../../pages/Settings/Notifications/Notifications';
import CustomApplications from '../../pages/Settings/CustomApplications/CustomApplications';
import LegalDocuments from '../../pages/Settings/LegalDocuments/LegalDocuments';
import ReviewRequestBusinessPartner from '../../pages/RequestBusinessPartner/ReviewRequestBusinessPartner';
import ReviewRequestChangeOwnership from '../../pages/RequestChangeOwnership/ReviewRequestChangeOwnership';
import { useOauthClients } from '../../pages/Apps/apps.queries';
import { AppBody } from '../../api/generated/pap';
import Apps from '../../pages/Apps/Apps';
import { useAppService } from '../../providers/app/app.context';
import { Aspect } from '../../api/generated/notification-pref';
import Licenses from '../../pages/Licenses/Licenses';
import ErrorPage from '../../pages/Error/ErrorPage';
import PendingMerges from '../../pages/Companies/PendingMerges';

const getOAuthClientText = (client: AppBody, t: ReturnType<typeof useTranslation>['t']) => {
  // Try translation key first
  const translationKey = `oauthClients.${client.name.toLowerCase()}`;
  const translatedText = t(translationKey, { ns: 'navigation' });
  // Return translation if found, otherwise fallback to client name
  return translatedText !== translationKey ? translatedText : client.name;
};

// Function to generate dynamic routes from OAuth clients
function generateOAuthClientRoutes(
  oauthClients: AppBody[],
  t: ReturnType<typeof useTranslation>['t']
): TopconRoute[] {
  return oauthClients
    .filter((client) => client.homepageURL && client.name) // Filter out clients without required fields
    .map((client) => ({
      // path: client.homepageURL,
      href: client.homepageURL,
      hrefTarget: client.target,
      postfixIcon: client.target === '_blank' ? 'open_in_new' : undefined,
      icon: (client.glyphIcon ?? 'apps') as IconName, // Default icon for OAuth client apps
      text: getOAuthClientText(client, t),
      hidden:
        !['topcon.apps.licenses', 'topcon.apps.sales'].includes(client.category) ||
        client.glyphIcon === 'licenses', // TODO: Remove old Licenses app on backend
      uiOnly: true,
    })) as TopconRoute[];
}

// Each helper binds a path to both its sidebar visibility and its guard, so the
// two always read the same `routeAccess` rule (ADR 0006).

/** Sidebar entry with its own page. */
function navRoute(path: AppRoutes, page: ReactNode, useIrrelevant?: () => boolean) {
  return {
    path,
    hidden: hideUnlessAccessible(path, useIrrelevant),
    element: <Authorize route={path}>{page}</Authorize>,
  };
}

/** Sidebar child entry; its page is registered separately with `pageRoute`. */
function navChild(path: AppRoutes, useIrrelevant?: () => boolean) {
  return { path, uiOnly: true, hidden: hideUnlessAccessible(path, useIrrelevant) };
}

/** Page reachable by URL (or a `navChild`) with no sidebar entry of its own. */
function pageRoute(path: AppRoutes, page: ReactNode) {
  return { path, hidden: true, element: <Authorize route={path}>{page}</Authorize> };
}

function NotificationSettingsPage() {
  const { currentBusinessPartner, loadedBusinessPartner } = useAppService();

  // The aspect follows the session type, which is unknown until the Business Partner loads.
  if (!loadedBusinessPartner) return null;

  return <NotificationSettings aspect={currentBusinessPartner ? Aspect.USER : Aspect.IDENTITY} />;
}

function LicensesRouteWrapper() {
  const { data: oauthClientsData } = useOauthClients();

  // Check if Correction Services client is available
  const correctionServicesClient = useMemo(() => {
    return oauthClientsData?.items.find((client) => client.name === 'Correction Services');
  }, [oauthClientsData]);

  // Construct the licenses URL the same way as in Licenses.tsx
  const licensesAppUrl = useMemo(() => {
    const licensesClient = oauthClientsData?.items.find(
      (client) => client.category === 'topcon.apps.licenses' && client.glyphIcon === 'devices'
    );
    if (licensesClient?.homepageURL) {
      const base = licensesClient.homepageURL.replace(/\/$/, '');
      return `${base}/licenses/inventory`;
    }
    return null;
  }, [oauthClientsData]);

  // If Correction Services is not available, redirect to the licenses URL
  useEffect(() => {
    if (!correctionServicesClient && licensesAppUrl) {
      window.location.href = licensesAppUrl;
    }
  }, [correctionServicesClient, licensesAppUrl]);

  // If redirecting, show nothing (or a loading state)
  if (!correctionServicesClient && licensesAppUrl) {
    return null;
  }

  // Otherwise, render the Licenses component
  return <Licenses />;
}
export const routes = [
  {
    ...navRoute(AppRoutes.Overview, <Overview />),
    icon: 'grid_view',
    text: t('overview', { ns: 'navigation' }),
  },
  {
    icon: 'manage_accounts',
    text: t('myProfile', { ns: 'navigation' }),
    uiOnly: true,
    hidden: true,
  },
  {
    ...navRoute(AppRoutes.Companies, <Companies />),
    icon: 'business',
    text: t('companies', { ns: 'navigation' }),
    children: [
      { ...navChild(AppRoutes.CompaniesList), text: t('companiesList', { ns: 'navigation' }) },
      { ...navChild(AppRoutes.Invites), text: t('invites', { ns: 'navigation' }) },
      { ...navChild(AppRoutes.JoinRequests), text: t('joinRequests', { ns: 'navigation' }) },
      {
        ...navChild(AppRoutes.UnclaimedOrganizations, useHideUnclaimedOrganizations),
        text: t('pendingMerges', { ns: 'navigation' }),
      },
    ],
  },
  pageRoute(AppRoutes.CompaniesList, <CompaniesList />),
  pageRoute(AppRoutes.JoinRequests, <JoinRequests />),
  pageRoute(AppRoutes.Invites, <Invites />),
  pageRoute(AppRoutes.UnclaimedOrganizations, <PendingMerges />),
  {
    ...navRoute(AppRoutes.Divisions, <Divisions />),
    icon: 'business',
    text: t('divisions', { ns: 'navigation' }),
  },
  {
    ...navRoute(AppRoutes.Users, <Users />),
    icon: 'account_circle',
    text: t('users', { ns: 'navigation' }),
    children: [{ ...navChild(AppRoutes.Requests), text: t('requests', { ns: 'navigation' }) }],
  },
  {
    ...navRoute(AppRoutes.Support, <Support />),
    icon: 'corporate_fare',
    text: t('support', { ns: 'navigation' }),
    children: [
      { ...navChild(AppRoutes.Organizations), text: t('organizations', { ns: 'navigation' }) },
      { ...navChild(AppRoutes.TopconId), text: t('topconId', { ns: 'navigation' }) },
    ],
  },
  pageRoute(AppRoutes.Organizations, <Organizations />),
  pageRoute(AppRoutes.TopconId, <TopconId />),
  {
    ...navRoute(AppRoutes.Customers, <CustomersHub />),
    icon: 'handshake',
    text: t('customers', { ns: 'navigation' }),
    children: [
      {
        ...navChild(AppRoutes.CustomersList),
        text: t('customersList', { ns: 'navigation' }),
      },
      {
        ...navChild(AppRoutes.CustomerInvites),
        text: t('customerInvites', { ns: 'navigation' }),
      },
      {
        ...navChild(AppRoutes.CustomerRequests),
        text: t('customerRequests', { ns: 'navigation' }),
      },
      {
        ...navChild(AppRoutes.CustomerSuggestions, useHideCustomerSuggestions),
        text: t('customerSuggestions', { ns: 'navigation' }),
      },
    ],
  },
  pageRoute(AppRoutes.CustomersList, <Customers />),
  pageRoute(AppRoutes.CustomerInvites, <CustomerInvites />),
  pageRoute(AppRoutes.CustomerRequests, <CustomerRequests />),
  pageRoute(AppRoutes.CustomerSuggestions, <CustomerSuggestions />),
  pageRoute(AppRoutes.Requests, <Requests />),
  {
    ...navRoute(AppRoutes.Apps, <Apps />),
    icon: 'apps',
    text: t('apps', { ns: 'navigation' }),
  },
  {
    // Hidden by useRoutes when there is no devices OAuth client (relevance, menu only).
    ...navRoute(AppRoutes.LicensesOverview, <LicensesRouteWrapper />),
    icon: 'license',
    text: t('licenses', { ns: 'navigation' }),
  },
  {
    ...navRoute(AppRoutes.Notifications, <Notifications />),
    icon: 'notifications',
    text: t('notifications', { ns: 'navigation' }),
  },

  {
    ...navRoute(AppRoutes.Settings, <Settings />),
    icon: 'settings',
    text: t('settings', { ns: 'navigation' }),
    children: [
      // BUSINESS PARTNER SETTINGS
      {
        ...navChild(AppRoutes.SettingsGeneralInformation),
        text: t('generalInformation', { ns: 'navigation' }),
      },
      { ...navChild(AppRoutes.SettingsAccess), text: t('access', { ns: 'navigation' }) },
      { ...navChild(AppRoutes.Dealers), text: t('dealers', { ns: 'navigation' }) },
      {
        ...navChild(AppRoutes.SettingsNotifications),
        text: t('notifications', { ns: 'navigation' }),
      },
      {
        ...navChild(AppRoutes.SettingsDomainRegistration),
        text: t('domainRegistration', { ns: 'navigation' }),
      },
      {
        ...navChild(AppRoutes.SettingsIdentityProviders),
        text: t('identityProviders', { ns: 'navigation' }),
      },
      {
        ...navChild(AppRoutes.SettingsCustomApps),
        text: t('customApplications', { ns: 'navigation' }),
      },
      // USER SETTINGS
      {
        ...navChild(AppRoutes.SettingsDeactivateAccount),
        text: t('deactivateAccount', { ns: 'navigation' }),
      },
      {
        ...navChild(AppRoutes.SettingsLegalDocuments),
        text: t('legalDocuments', { ns: 'navigation' }),
      },
    ],
  },
  pageRoute(AppRoutes.SettingsGeneralInformation, <GeneralInformation />),
  pageRoute(AppRoutes.SettingsAccess, <Access />),
  pageRoute(AppRoutes.SettingsDeactivateAccount, <DeactivateAccount />),
  pageRoute(AppRoutes.SettingsDomainRegistration, <DomainRegistration />),
  pageRoute(AppRoutes.Dealers, <Dealers />),
  pageRoute(AppRoutes.SettingsIdentityProviders, <IdentityProviders />),
  pageRoute(AppRoutes.SettingsNotifications, <NotificationSettingsPage />),
  pageRoute(AppRoutes.SettingsCustomApps, <CustomApplications />),
  pageRoute(AppRoutes.SettingsLegalDocuments, <LegalDocuments />),
  pageRoute(AppRoutes.NoBP, <AboutMe />),
  pageRoute(AppRoutes.AboutMe, <AboutMe />),
  {
    path: AppRoutes.Playground,
    hidden: !import.meta.env.DEV,
    icon: 'lightbulb_outline',
    text: t('playground', { ns: 'navigation' }),
    slot: 'footer',
    element: (
      <Authorize>
        <Playground />
      </Authorize>
    ),
  },
  {
    path: AppRoutes.DevSettings,
    hidden: !import.meta.env.DEV,
    icon: 'flutter_dash',
    text: t('devSettings', { ns: 'navigation' }),
    slot: 'footer',
    element: (
      <Authorize>
        <DevSettings />
      </Authorize>
    ),
  },
  {
    path: AppRoutes.ServiceAgreement,
    hidden: true,
    element: <ServiceAgreement />,
  },
  {
    path: AppRoutes.ReviewRequestBussinessPartner,
    hidden: true,
    element: <ReviewRequestBusinessPartner />,
  },
  {
    path: AppRoutes.ReviewRequestChangeOwnership,
    hidden: true,
    element: <ReviewRequestChangeOwnership />,
  },
  {
    path: AppRoutes.Error,
    hidden: true,
    element: <ErrorPage />,
  },
  {
    path: AppRoutes.Wildcard,
    hidden: true,
    element: <NotFound />,
  },
] as TopconRoute[];

// Hook to generate routes with OAuth clients
export function useRoutes(): TopconRoute[] {
  const { data: oauthClientsData } = useOauthClients();
  const { isIdentifiedThroughPing, pingIdentityBaseUrl, currentBusinessPartner } = useAppService();
  const { t } = useTranslation('navigation');

  return useMemo(() => {
    const oauthClientRoutes = oauthClientsData?.items
      ? generateOAuthClientRoutes(oauthClientsData.items, t)
      : [];

    const resolvedRoutes = routes.map((route) => {
      const isProfileRoute = route.uiOnly && route.icon === 'manage_accounts';
      if (isProfileRoute) {
        // if ping is enabled, point to external myaccount link and open in new tab
        if (isIdentifiedThroughPing && pingIdentityBaseUrl) {
          return {
            ...route,
            href: `${pingIdentityBaseUrl}/myaccount/#profile`,
            hrefTarget: '_blank' as const,
            rel: 'noopener noreferrer',
            postfixIcon: 'open_in_new',
            hidden: !isIdentifiedThroughPing || !!currentBusinessPartner,
          };
        }
      }
      return route;
    });

    // CSSD-1086 - Specific order of routes for navigation.
    // Using glyphIcon / icon as identifier for the routes until we have a better ordering solution.

    // Find "devices" route from oauthClientRoutes by icon
    const devicesRouteIndex = oauthClientRoutes.findIndex((route) => route.icon === 'devices');
    const devicesRoute = devicesRouteIndex !== -1 ? oauthClientRoutes[devicesRouteIndex] : null;
    const remainingOAuthRoutes = devicesRoute
      ? oauthClientRoutes.filter((_, index) => index !== devicesRouteIndex)
      : oauthClientRoutes;

    // Find "apps" route index by icon in resolvedRoutes
    const appsRouteIndex = resolvedRoutes.findIndex((route) => route.icon === 'apps');

    // Find "settings" route by icon
    const settingsRouteIndex = resolvedRoutes.findIndex((route) => route.icon === 'settings');
    const settingsRoute = settingsRouteIndex !== -1 ? resolvedRoutes[settingsRouteIndex] : null;

    // Licenses is not relevant without a devices route: hide the menu entry only.
    // A direct URL still renders LicensesRouteWrapper (ADR 0006: relevance never blocks).
    const resolvedRoutesWithLicenseHidden = resolvedRoutes.map((route) => {
      if (route.icon === 'license' && !devicesRoute) {
        return { ...route, hidden: true };
      }
      return route;
    });

    // Remove settings from resolvedRoutes to place it at the end later
    const routesWithoutSettings = settingsRoute
      ? resolvedRoutesWithLicenseHidden.filter((_, index) => index !== settingsRouteIndex)
      : resolvedRoutesWithLicenseHidden;

    // Build final routes array
    let finalRoutes = [...routesWithoutSettings];

    // Insert devices after apps if both exist
    // Adjust appsRouteIndex based on whether settings was removed before it
    if (devicesRoute && appsRouteIndex !== -1) {
      // If settings was before apps, we need to account for its removal
      const adjustedAppsIndex =
        settingsRoute && settingsRouteIndex < appsRouteIndex ? appsRouteIndex - 1 : appsRouteIndex;
      const insertIndex = adjustedAppsIndex + 1;
      finalRoutes.splice(insertIndex, 0, devicesRoute);
    } else if (devicesRoute) {
      // If apps not found, just add devices to the end of resolved routes
      finalRoutes.push(devicesRoute);
    }

    // Add remaining OAuth routes
    finalRoutes = [...finalRoutes, ...remainingOAuthRoutes];

    // Ensure settings is last
    if (settingsRoute) {
      finalRoutes.push(settingsRoute);
    }

    return finalRoutes;
  }, [oauthClientsData, t, isIdentifiedThroughPing, pingIdentityBaseUrl, currentBusinessPartner]);
}
